
import { useQuery } from '../../../lib/hooks/useQuery';
import { useMutation } from '../../../lib/hooks/useMutation';
import { invoicesApi } from '../../../lib/api/invoices';
import { Invoice, InvoiceFilters } from '../../../lib/types';
import { supabase } from '../../../lib/supabase';

export function useBilling(filters?: InvoiceFilters) {
  const invoicesQuery = useQuery(
    ['invoices', JSON.stringify(filters)],
    () => invoicesApi.getInvoices(filters)
  );

  const statsQuery = useQuery(
    ['invoices', 'stats'],
    () => invoicesApi.getInvoiceStats()
  );

  return {
    invoices: invoicesQuery.data || [],
    isLoading: invoicesQuery.isLoading,
    error: invoicesQuery.error,
    refetch: invoicesQuery.refetch,
    billingStats: statsQuery.data || {
      totalInvoices: 0,
      totalAmount: 0,
      paidInvoices: 0,
      pendingInvoices: 0,
      overdueInvoices: 0
    },
    statsLoading: statsQuery.isLoading,
    // Add rates functionality
    rates: useRates(),
    setRate: useSetRate()
  };
}

export function useRates() {
  return useQuery(
    ['rates'],
    async () => {
      const { data, error } = await supabase
        .from('gold_rates')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  );
}

export function useSetRate() {
  return useMutation(
    async (rateData: any) => {
      // First, deactivate all existing rates for the same metal
      await supabase
        .from('gold_rates')
        .update({ isActive: false })
        .eq('metal', rateData.metal);

      // Then insert the new rate
      const { data, error } = await supabase
        .from('gold_rates')
        .insert({
          ...rateData,
          isActive: true,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  );
}

export function useInvoice(id: string) {
  return useQuery(
    ['invoices', id],
    () => invoicesApi.getById<Invoice>(id),
    { enabled: !!id }
  );
}

export function useInvoiceSearch() {
  return useMutation(
    (searchTerm: string) => invoicesApi.searchInvoices(searchTerm)
  );
}

export function useCreateInvoice() {
  return useMutation(
    (invoiceData: Partial<Invoice>) => invoicesApi.create<Invoice>(invoiceData),
    {
      onSuccess: () => {
        console.log('Invoice created successfully');
      }
    }
  );
}

export function useUpdateInvoice() {
  return useMutation(
    ({ id, data }: { id: string; data: Partial<Invoice> }) => 
      invoicesApi.update<Invoice>(id, data),
    {
      onSuccess: () => {
        console.log('Invoice updated successfully');
      }
    }
  );
}

export function useDeleteInvoice() {
  return useMutation(
    (id: string) => invoicesApi.delete(id),
    {
      onSuccess: () => {
        console.log('Invoice deleted successfully');
      }
    }
  );
}

export function useGenerateInvoiceNumber() {
  return useMutation(
    () => invoicesApi.generateInvoiceNumber()
  );
}
